<!-- eslint-disable -->
<template>
  <!-- Floating Reader Mode Button (always visible when enabled) -->
  <v-btn
    v-if="accessibilitySettings.readerModeEnabled"
    class="reader-mode-fab"
    color="primary"
    size="large"
    icon
    elevation="8"
    :title="$t('accessibility.readerMode')"
    @click="openReaderMode"
  >
    <v-icon size="28">mdi-book-open-page-variant</v-icon>
  </v-btn>

  <!-- Fullscreen Reader Dialog -->
  <v-dialog v-model="showReader" fullscreen transition="dialog-bottom-transition">
    <v-card class="reader-card" :class="{ 'high-contrast-reader': accessibilitySettings.highContrast }">
      <!-- Top toolbar -->
      <v-toolbar color="primary" density="comfortable">
        <v-btn icon @click="showReader = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          <v-icon class="mr-2">mdi-book-open-page-variant</v-icon>
          {{ $t('accessibility.readerMode') }}
          <span v-if="currentGroupName" class="ml-2 text-body-1">– {{ currentGroupName }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Font size controls -->
        <v-btn icon @click="decreaseFont" :disabled="readerFontSize <= 18" :title="$t('accessibility.fontDecrease')">
          <v-icon>mdi-format-font-size-decrease</v-icon>
        </v-btn>
        <span class="text-body-2 mx-1">{{ readerFontSize }}px</span>
        <v-btn icon @click="increaseFont" :disabled="readerFontSize >= 48" :title="$t('accessibility.fontIncrease')">
          <v-icon>mdi-format-font-size-increase</v-icon>
        </v-btn>

        <!-- Read aloud current post -->
        <v-btn 
          v-if="accessibilitySettings.textToSpeechEnabled"
          icon 
          @click="readCurrentPost"
          :title="$t('accessibility.readAloud')"
        >
          <v-icon>mdi-text-to-speech</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Posts content -->
      <v-card-text class="reader-content pa-0">
        <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>

        <div v-else-if="readerPosts.length === 0" class="d-flex flex-column justify-center align-center" style="min-height: 300px;">
          <v-icon size="64" color="grey">mdi-post-outline</v-icon>
          <p class="text-h6 text-grey mt-4">{{ $t('wall.subtitle') }}</p>
        </div>

        <div v-else class="reader-posts">
          <div
            v-for="(post, index) in readerPosts"
            :key="post.postId"
            class="reader-post"
            :ref="el => { if (el) postRefs[index] = el }"
          >
            <!-- Post header -->
            <div class="reader-post-header">
              <div class="reader-post-author">
                <v-avatar :color="post.avatarUrl ? undefined : 'primary'" size="56">
                  <v-img v-if="post.avatarUrl" :src="post.avatarUrl"></v-img>
                  <span v-else class="text-white text-h6">{{ (post.displayName || post.username || '?').charAt(0) }}</span>
                </v-avatar>
                <div class="ml-4">
                  <div class="reader-author-name">{{ post.displayName || post.username }}</div>
                  <div class="reader-post-time">{{ formatDate(post.created_at) }}</div>
                </div>
              </div>
              
              <!-- Read this post aloud -->
              <v-btn
                v-if="accessibilitySettings.textToSpeechEnabled"
                icon
                variant="text"
                :size="Math.max(40, readerFontSize * 1.4)"
                @click="readPost(post)"
                :title="$t('accessibility.readAloud')"
              >
                <v-icon :size="Math.max(24, readerFontSize)">mdi-volume-high</v-icon>
              </v-btn>
            </div>

            <!-- Post content -->
            <div class="reader-post-content" :style="{ fontSize: readerFontSize + 'px' }" v-html="formatInline(post.content)"></div>

            <!-- Post image -->
            <div v-if="post.imageUrl" class="reader-post-image">
              <v-img :src="post.imageUrl" max-height="500" contain></v-img>
            </div>

            <!-- Multiple images -->
            <div v-if="post.images && post.images.length > 0" class="reader-post-images">
              <v-img 
                v-for="(img, imgIdx) in post.images" 
                :key="imgIdx"
                :src="img.imageUrl || img"
                max-height="400"
                contain
                class="mb-2"
              ></v-img>
            </div>

            <!-- Reactions summary -->
            <div class="reader-post-stats">
              <span v-if="post.likesCount">❤️ {{ post.likesCount }}</span>
              <span v-if="post.commentsCount" class="cursor-pointer" @click="toggleComments(post)">
                💬 {{ post.commentsCount }}
                <v-icon size="16" class="ml-1">{{ post.showComments ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </span>
            </div>

            <!-- Comments (expandable) -->
            <div v-if="post.showComments" class="reader-comments">
              <div v-if="post.loadingComments" class="text-center py-4">
                <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
              </div>
              <div v-else-if="post.comments && post.comments.length > 0">
                <div 
                  v-for="comment in post.comments" 
                  :key="comment.commentId"
                  class="reader-comment"
                >
                  <div class="reader-comment-header">
                    <v-avatar :color="comment.avatarUrl ? undefined : 'secondary'" size="36">
                      <v-img v-if="comment.avatarUrl" :src="comment.avatarUrl"></v-img>
                      <span v-else class="text-white text-body-2">{{ (comment.displayName || comment.username || '?').charAt(0) }}</span>
                    </v-avatar>
                    <div class="ml-3">
                      <span class="reader-comment-author">{{ comment.displayName || comment.username }}</span>
                      <span class="reader-comment-time ml-2">{{ formatDate(comment.created_at) }}</span>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="accessibilitySettings.textToSpeechEnabled"
                      icon
                      variant="text"
                      :size="Math.max(32, readerFontSize * 1.1)"
                      @click="readComment(comment)"
                    >
                      <v-icon :size="Math.max(18, readerFontSize * 0.7)">mdi-volume-high</v-icon>
                    </v-btn>
                  </div>
                  <div 
                    class="reader-comment-content" 
                    :style="{ fontSize: Math.max(18, readerFontSize * 0.85) + 'px' }"
                    v-html="formatInline(comment.content)"
                  ></div>
                </div>
              </div>
              <div v-else class="text-center py-2 text-grey">
                {{ $t('common.noComments') }}
              </div>
            </div>

            <!-- Divider between posts -->
            <v-divider v-if="index < readerPosts.length - 1" class="my-2"></v-divider>
          </div>
        </div>
      </v-card-text>

      <!-- Bottom navigation -->
      <v-footer app class="reader-footer pa-2">
        <v-btn 
          variant="tonal" 
          @click="scrollToPrev"
          :disabled="currentPostIndex <= 0"
          size="large"
        >
          <v-icon start>mdi-chevron-up</v-icon>
          {{ $t('accessibility.prevPost') }}
        </v-btn>
        <v-spacer></v-spacer>
        <span class="text-body-1">
          {{ currentPostIndex + 1 }} / {{ readerPosts.length }}
        </span>
        <v-spacer></v-spacer>
        <v-btn 
          variant="tonal" 
          @click="scrollToNext"
          :disabled="currentPostIndex >= readerPosts.length - 1"
          size="large"
        >
          {{ $t('accessibility.nextPost') }}
          <v-icon end>mdi-chevron-down</v-icon>
        </v-btn>
      </v-footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
/* eslint-disable */
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { accessibilitySettings, speak, stopSpeaking } from '../services/accessibility'
import { formatInline } from '../utils/formatText'
import api from '../services/api'

const route = useRoute()
const store = useStore()
const { t } = useI18n()

const showReader = ref(false)
const readerPosts = ref([])
const loading = ref(false)
const readerFontSize = ref(28)
const currentPostIndex = ref(0)
const postRefs = ref({})
const currentGroupName = ref('')

const openReaderMode = async () => {
  showReader.value = true
  loading.value = true
  currentPostIndex.value = 0
  postRefs.value = {}

  try {
    // Determine context: are we in a group or on the wall?
    const routeName = route.name
    const groupId = route.params?.id

    if (routeName === 'group' && groupId) {
      // Load group posts
      const response = await api.get(`/groups/${groupId}/posts`)
      readerPosts.value = response.data.posts || []
      // Try to get group name
      currentGroupName.value = response.data.groupName || ''
      if (!currentGroupName.value) {
        try {
          const gRes = await api.get(`/groups/${groupId}`)
          currentGroupName.value = gRes.data.group?.groupName || ''
        } catch { /* ignore */ }
      }
    } else {
      // Load wall posts (use store if available, otherwise fetch)
      const storePosts = store.getters['posts/posts']
      if (storePosts && storePosts.length > 0) {
        readerPosts.value = [...storePosts]
      } else {
        const response = await api.get('/posts')
        readerPosts.value = response.data.posts || []
      }
      currentGroupName.value = ''
    }
  } catch (error) {
    console.error('Reader mode: failed to load posts', error)
    readerPosts.value = []
  } finally {
    loading.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const increaseFont = () => {
  if (readerFontSize.value < 48) {
    readerFontSize.value += 2
  }
}

const decreaseFont = () => {
  if (readerFontSize.value > 18) {
    readerFontSize.value -= 2
  }
}

const readPost = (post) => {
  const author = post.displayName || post.username || ''
  const text = `${author} ${t('accessibility.wrote')}: ${post.content}`
  speak(text)
}

const readCurrentPost = () => {
  if (readerPosts.value[currentPostIndex.value]) {
    readPost(readerPosts.value[currentPostIndex.value])
  }
}

const readComment = (comment) => {
  const author = comment.displayName || comment.username || ''
  const text = `${author} ${t('accessibility.wrote')}: ${comment.content}`
  speak(text)
}

const toggleComments = async (post) => {
  if (post.showComments) {
    post.showComments = false
    return
  }

  post.showComments = true

  // Only load if not already loaded
  if (!post.comments) {
    post.loadingComments = true
    try {
      const response = await api.get(`/posts/${post.postId}/comments`)
      post.comments = response.data.comments || []
    } catch (error) {
      console.error('Failed to load comments:', error)
      post.comments = []
    } finally {
      post.loadingComments = false
    }
  }
}

const scrollToPost = async (index) => {
  currentPostIndex.value = index
  await nextTick()
  const el = postRefs.value[index]
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const scrollToNext = () => {
  if (currentPostIndex.value < readerPosts.value.length - 1) {
    scrollToPost(currentPostIndex.value + 1)
  }
}

const scrollToPrev = () => {
  if (currentPostIndex.value > 0) {
    scrollToPost(currentPostIndex.value - 1)
  }
}

// Stop speech when closing
watch(showReader, (open) => {
  if (!open) {
    stopSpeaking()
  }
})

// Keyboard navigation
const handleKeydown = (e) => {
  if (!showReader.value) return
  
  if (e.key === 'ArrowDown' || e.key === 'PageDown') {
    e.preventDefault()
    scrollToNext()
  } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
    e.preventDefault()
    scrollToPrev()
  } else if (e.key === 'Escape') {
    showReader.value = false
  } else if (e.key === ' ' && accessibilitySettings.textToSpeechEnabled) {
    e.preventDefault()
    readCurrentPost()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  stopSpeaking()
})
</script>

<style scoped>
.reader-mode-fab {
  position: fixed !important;
  bottom: 95px;
  right: 26px;
  z-index: 1000;
}

.reader-card {
  background: rgb(var(--v-theme-background));
  color: rgb(var(--v-theme-on-background));
}

.reader-content {
  overflow-y: auto;
  height: calc(100vh - 64px - 56px);
  padding-bottom: 60px;
}

.reader-posts {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 16px;
}

.reader-post {
  padding: 24px 0;
}

.reader-post-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.reader-post-author {
  display: flex;
  align-items: center;
}

.reader-author-name {
  font-size: 1.3em;
  font-weight: 600;
  line-height: 1.3;
}

.reader-post-time {
  font-size: 0.95em;
  opacity: 0.7;
}

.reader-post-content {
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-bottom: 16px;
}

.reader-post-image {
  margin: 16px 0;
  border-radius: 8px;
  overflow: hidden;
}

.reader-post-images {
  margin: 16px 0;
}

.reader-post-stats {
  display: flex;
  gap: 16px;
  font-size: 1.1em;
  opacity: 0.8;
  margin-top: 12px;
}

.reader-comments {
  margin-top: 16px;
  padding: 16px;
  background: rgba(var(--v-theme-on-background), 0.04);
  border-radius: 12px;
}

.reader-comment {
  padding: 12px 0;
}

.reader-comment + .reader-comment {
  border-top: 1px solid rgba(var(--v-theme-on-background), 0.08);
}

.reader-comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.reader-comment-author {
  font-weight: 600;
  font-size: 1.05em;
}

.reader-comment-time {
  font-size: 0.85em;
  opacity: 0.6;
}

.reader-comment-content {
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding-left: 48px;
}

.cursor-pointer {
  cursor: pointer;
}

.reader-footer {
  background: rgb(var(--v-theme-surface)) !important;
  border-top: 1px solid rgba(var(--v-border-color), 0.12);
}

/* High contrast overrides */
.high-contrast-reader {
  background: #000 !important;
  color: #fff !important;
}

.high-contrast-reader .reader-post-content {
  color: #ffff00;
}

.high-contrast-reader .reader-author-name {
  color: #00ffff;
}

.high-contrast-reader .reader-post-time {
  color: #aaa;
}

.high-contrast-reader .reader-comments {
  background: #111;
}

.high-contrast-reader .reader-comment-content {
  color: #ffff00;
}

.high-contrast-reader .reader-comment-author {
  color: #00ffff;
}

/* Make sure the FAB doesn't overlap chat widget */
@media (max-width: 600px) {
  .reader-mode-fab {
    bottom: 140px;
    right: 12px;
  }
  
  .reader-posts {
    padding: 16px 12px;
  }
}
</style>